/**
 * Properties API
 * RTK Query endpoints for property management
 */

import { baseApi } from "./baseApi";
import type { RentType } from "../../types";

/**
 * Property interface matching backend
 */
export interface Property {
	id: string;
	organization?: string;
	organization_name?: string;
	name: string;
	address: string;
	city?: string;
	state?: string;
	zip_code?: string;
	unit_number?: string;
	country?: string;
	property_type:
		| "apartment"
		| "house"
		| "condo"
		| "commercial"
		| "office"
		| "other";
	status: "available" | "pending" | "leased" | "sold" | "maintenance";
	bedrooms?: number;
	bathrooms?: number;
	square_feet?: number;
	rent_amount?: number;
	rent_type?: RentType;
	initial_payment_duration?: number;
	monthly_rent?: number; // Legacy field
	purchase_price?: number;
	security_deposit?: number;
	description?: string;
	features?: string[];
	images: string[];
	created_at: string;
	updated_at: string;
}

/**
 * Property list response interface
 */
export interface PropertyListResponse {
	count: number;
	next: string | null;
	previous: string | null;
	results: Property[];
}

/**
 * Create/Update property request interface
 */
export interface PropertyData {
	organization?: string; // Optional - backend will use user's organization if not provided
	name: string;
	address: string;
	city?: string;
	state?: string;
	zip_code?: string;
	unit_number?: string;
	country?: string;
	property_type:
		| "apartment"
		| "house"
		| "condo"
		| "commercial"
		| "office"
		| "other";
	status?: "available" | "pending" | "leased" | "sold" | "maintenance";
	bedrooms?: number;
	bathrooms?: number;
	square_feet?: number;
	rent_amount?: number;
	rent_type?: RentType;
	initial_payment_duration?: number;
	monthly_rent?: number; // Legacy field
	purchase_price?: number;
	security_deposit?: number;
	description?: string;
	features?: string[];
	images?: string[];
	image_files?: string[]; // Base64 encoded images for upload
}

/**
 * Properties API endpoints
 */
export const propertiesApi = baseApi.injectEndpoints({
	endpoints: (builder) => ({
		/**
		 * List all properties (with filters, search, pagination)
		 */
		listProperties: builder.query<
			PropertyListResponse,
			{
				page?: number;
				page_size?: number;
				status?: string;
				property_type?: string;
				city?: string;
				state?: string;
				search?: string;
				ordering?: string;
			}
		>({
			query: (params = {}) => {
				const queryParams = new URLSearchParams();
				if (params.page) queryParams.append("page", params.page.toString());
				if (params.page_size)
					queryParams.append("page_size", params.page_size.toString());
				if (params.status) queryParams.append("status", params.status);
				if (params.property_type)
					queryParams.append("property_type", params.property_type);
				if (params.city) queryParams.append("city", params.city);
				if (params.state) queryParams.append("state", params.state);
				if (params.search) queryParams.append("search", params.search);
				if (params.ordering) queryParams.append("ordering", params.ordering);

				const queryString = queryParams.toString();
				return {
					url: queryString ? `/properties/?${queryString}` : `/properties/`,
					method: "GET",
				};
			},
			transformResponse: (response: {
				count: number;
				next: string | null;
				previous: string | null;
				results: any[];
			}) => {
				// Use backend snake_case directly - no transformation needed
				return {
					...response,
					results:
						response.results?.map((prop: any) => ({
							...prop,
							// Ensure images is always an array
							images: Array.isArray(prop.images)
								? prop.images
								: prop.thumbnail_image
								? [prop.thumbnail_image]
								: [],
							// Ensure features is always an array
							features: Array.isArray(prop.features)
								? prop.features.filter(
										(f: any) => f && String(f).trim().length > 0,
								  )
								: [],
						})) || [],
				};
			},
			providesTags: ["Properties"],
		}),

		/**
		 * Get available properties
		 * Note: FastAPI doesn't have a dedicated endpoint, so we filter by status
		 */
		getAvailableProperties: builder.query<Property[], void>({
			query: () => ({
				url: "/properties/?status=available",
				method: "GET",
			}),
			transformResponse: (response: PropertyListResponse) =>
				response.results || [],
			providesTags: ["Properties"],
		}),

		/**
		 * Get property by ID
		 */
		getProperty: builder.query<Property, string>({
			query: (id) => ({
				url: `/properties/${id}`,
				method: "GET",
			}),
			transformResponse: (response: any) => {
				// Transform backend snake_case to frontend camelCase
				return {
					...response,
					organization: response.organization_id, // Map organization_id to organization
					// Ensure images is always an array
					images: Array.isArray(response.images)
						? response.images
						: response.thumbnail_image
						? [response.thumbnail_image]
						: [],
					// Ensure features is always an array
					features: Array.isArray(response.features)
						? response.features.filter(
								(f: any) => f && String(f).trim().length > 0,
						  )
						: [],
				};
			},
			providesTags: (result, error, id) => [{ type: "Property", id }],
		}),

		/**
		 * Create new property (admin only)
		 * Extended timeout for image uploads
		 */
		createProperty: builder.mutation<Property, PropertyData>({
			query: (data) => ({
				url: "/properties/",
				method: "POST",
				body: data,
				timeout: 120000, // 2 minutes for image uploads
			}),
			invalidatesTags: ["Properties"],
		}),

		/**
		 * Update property (admin only)
		 * Extended timeout for image uploads
		 */
		updateProperty: builder.mutation<
			Property,
			{ id: string; data: Partial<PropertyData> }
		>({
			query: ({ id, data }) => ({
				url: `/properties/${id}`,
				method: "PATCH",
				body: data,
				timeout: 120000, // 2 minutes for image uploads
			}),
			invalidatesTags: (result, error, { id }) => [
				{ type: "Property", id },
				"Properties",
			],
		}),

		/**
		 * Delete property (admin only)
		 * Uses optimistic update for snappier UI
		 */
		deleteProperty: builder.mutation<void, string>({
			query: (id) => ({
				url: `/properties/${id}`,
				method: "DELETE",
			}),
			invalidatesTags: ["Properties"],
			async onQueryStarted(id, { dispatch, queryFulfilled }) {
				const patchResults: Array<() => void> = [];

				// Optimistically remove property from the first page of listProperties
				patchResults.push(
					dispatch(
						propertiesApi.util.updateQueryData(
							"listProperties",
							{ page: 1, page_size: 20 },
							(draft) => {
								if (!draft?.results) return;
								draft.results = draft.results.filter(
									(property) => property.id !== id,
								);
								draft.count = Math.max(0, draft.count - 1);
							},
						),
					).undo,
				);

				try {
					await queryFulfilled;
				} catch {
					// Roll back optimistic updates on failure
					patchResults.forEach((undo) => undo());
				}
			},
		}),

		/**
		 * Get properties by type
		 * Note: FastAPI doesn't have a dedicated endpoint, so we filter by property_type
		 */
		getPropertiesByType: builder.query<
			Property[],
			"apartment" | "house" | "condo" | "commercial" | "office" | "other"
		>({
			query: (type) => ({
				url: `/properties/?property_type=${type}`,
				method: "GET",
			}),
			transformResponse: (response: PropertyListResponse) =>
				response.results || [],
			providesTags: ["Properties"],
		}),
	}),
});

/**
 * Export hooks for usage in functional components
 */
export const {
	useListPropertiesQuery,
	useLazyListPropertiesQuery,
	useGetAvailablePropertiesQuery,
	useGetPropertyQuery,
	useCreatePropertyMutation,
	useUpdatePropertyMutation,
	useDeletePropertyMutation,
	useGetPropertiesByTypeQuery,
} = propertiesApi;
